import { Close, Monitor, CameraIndoor, PhotoCameraFront, Smartphone } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Rnd } from 'react-rnd';
import { makeStyles } from 'tss-react/mui';
import { ENTITIES, LS_PIP_STATE, STREAM_TYPES } from '../consts';
import { messages } from '../intl';
import { removePip, setActivePip } from '../store/ui/actions';
import { getLastActivePipId, getPips, getTypeFilter } from '../store/ui/selectors';
import useInterval from '../utils/useInterval';
import VideoBox from './ExamPreview/VideoBox';
import { getMainVideo } from './ExamPreview/VideoControlBar';

const useStyles = makeStyles()((theme) => ({
	bounding: {
		position: 'fixed',
		'& .react-draggable': { overflow: 'hidden', boxShadow: '0 0 20px #666', height: 'fit-content !important' },
		'& .dot': { top: '23px' },
		'& .resize': { left: '9px', position: 'absolute', pointerEvents: 'none', zIndex: 120, top: ' 8px' },
		'& .source': {
			position: 'absolute',
			top: '8px',
			zIndex: 10,
			display: 'flex',
			gap: '10px',
			left: '12px',
			color: 'white',
			'& div': { cursor: 'pointer' },
			'& svg': { color: 'white' },
		},
		'& .title': {
			color: 'white',
			position: 'absolute',
			top: 0,
			background: theme.palette.primary.main,
			width: 'calc(100% - 20px)',
			left: '0',
			padding: '8px 10px 5px 10px',
			display: 'flex',
			zIndex: 1,
			pointerEvents: 'none',
			justifyContent: 'center',
		},
		'& .close': {
			position: 'absolute',
			top: '8px',
			zIndex: 10,
			display: 'flex',
			cursor: 'pointer',
			gap: '10px',
			right: '12px',
			'& svg': { color: 'white' },
		},
		'& .videoContainer': {
			width: '100% !important',
			paddingTop: '32px',
			background: 'black',
			overflow: 'hidden',
			'& video': { width: '100% !important', height: 'auto !important' },
		},
		// Neutralize the PiP layout (fixed padding + auto video height) once the container goes
		// fullscreen, so it matches the main video's fullscreen sizing (see VideoBox.js `.fullscreen`)
		'& .videoContainer.fullscreen': {
			paddingTop: 0,
			'& video': { height: '88vh !important' },
		},
		'& .control-bar': {
			background: 'white',
			padding: '10px',
		},
		'& .MuiPopover-root': {
			position: 'absolute !important',
			'& .MuiPaper-root': {
				position: 'absolute !important',
				maxHeight: '230px !important',
				top: '-130px !important',
				left: '0 !important',
			},
		},
	},
}));

const PIP_MIN_WIDTH = 460;
const PIP_TITLE_BAR_HEIGHT = 32;
const PIP_CONTROL_BAR_HEIGHT = 162;
const PIP_BARS_HEIGHT = PIP_TITLE_BAR_HEIGHT + PIP_CONTROL_BAR_HEIGHT;
const PIP_MIN_HEIGHT = PIP_BARS_HEIGHT + 50;
const PIP_MARGIN = 10;
// Cascade offset for the 2nd slot's default position - kept negative (see rndDefault below)
const PIP_SLOT_CASCADE_OFFSET = 40;
// Drift tolerance before a PiP video gets re-synced to the main video's time
const PIP_SYNC_TOLERANCE_SECONDS = 0.3;

// Slot 1 keeps the plain key for compatibility with state saved before multi-PiP existed
const getLsKey = (pipId) => (pipId === 1 ? LS_PIP_STATE : LS_PIP_STATE + pipId);

const loadSavedState = (lsKey) => {
	try {
		const raw = localStorage.getItem(lsKey);
		if (!raw) return null;
		const { x, y, width, height } = JSON.parse(raw);
		if (![x, y, width, height].every((v) => typeof v === 'number')) return null;
		if (width < PIP_MIN_WIDTH || height < PIP_MIN_HEIGHT) return null;
		return { x, y, width, height };
	} catch {
		return null;
	}
};

const fitToViewport = ({ x, y, width, height }) => {
	let w = Math.min(width, window.innerWidth - 2 * PIP_MARGIN);
	let h = Math.round(((height - PIP_BARS_HEIGHT) * w) / width) + PIP_BARS_HEIGHT;

	const maxHeight = window.innerHeight - 2 * PIP_MARGIN;
	if (h > maxHeight) {
		w = Math.round((w * (maxHeight - PIP_BARS_HEIGHT)) / (h - PIP_BARS_HEIGHT));
		h = maxHeight;
	}

	w = Math.max(w, PIP_MIN_WIDTH);
	h = Math.max(h, PIP_MIN_HEIGHT);

	return {
		x: Math.max(0, Math.min(x, window.innerWidth - w - PIP_MARGIN)),
		y: Math.max(0, Math.min(y, window.innerHeight - h - PIP_MARGIN)),
		width: w,
		height: h,
	};
};

const saveState = (lsKey, x, y, node) => {
	if (!node) return;
	localStorage.setItem(lsKey, JSON.stringify({ x, y, width: node.offsetWidth, height: node.offsetHeight }));
};

const getMainVideoPipDimensions = () => {
	const video = getMainVideo();
	if (!video || !video.videoWidth || !video.videoHeight) return { width: PIP_MIN_WIDTH, height: PIP_MIN_HEIGHT };

	const videoRatio = video.videoWidth / video.videoHeight;
	const elementRatio = video.clientWidth / video.clientHeight;

	const contentWidth = videoRatio > elementRatio ? video.clientWidth : Math.round(video.clientHeight * videoRatio);
	const width = Math.max(contentWidth, PIP_MIN_WIDTH);
	const height = PIP_TITLE_BAR_HEIGHT + Math.round(width / videoRatio) + PIP_CONTROL_BAR_HEIGHT;

	return { width, height };
};

const Pip = ({ pipId }) => {
	const { classes } = useStyles();
	const intl = useIntl();
	const dispatch = useDispatch();
	const typeFilter = useSelector(getTypeFilter);
	const [pipTypeFilter, setPipTypeFilter] = useState(typeFilter);
	const lastActivePipId = useSelector(getLastActivePipId);
	const isActive = pipId === lastActivePipId;

	const lsKey = useMemo(() => getLsKey(pipId), [pipId]);
	const fallback = useMemo(() => getMainVideoPipDimensions(), []);
	const saved = useMemo(() => loadSavedState(lsKey), [lsKey]);

	// Slot 2 defaults slightly up/left of slot 1's bottom-right corner - a positive cascade offset
	// would get clamped straight back to the same spot since that corner is fitToViewport's upper bound
	const slotOffset = pipId === 2 ? PIP_SLOT_CASCADE_OFFSET : 0;

	const rndDefault = fitToViewport(
		saved ?? {
			...fallback,
			x: window.innerWidth - fallback.width - PIP_MARGIN - slotOffset,
			y: window.innerHeight - fallback.height - PIP_MARGIN - slotOffset,
		}
	);

	const handleActivate = () => {
		if (!isActive) dispatch(setActivePip(pipId));
	};

	return (
		<div
			className={classes.bounding}
			style={{ top: 0, left: 0, zIndex: 1250 + (isActive ? 1 : 0) }}
			onMouseDown={handleActivate}
		>
			<Rnd
				default={rndDefault}
				minWidth={PIP_MIN_WIDTH}
				minHeight={PIP_MIN_HEIGHT}
				onDragStop={(e, d) => saveState(lsKey, d.x, d.y, d.node)}
				onResizeStop={(e, dir, ref, delta, pos) => saveState(lsKey, pos.x, pos.y, ref)}
			>
				<div className="source">
					<div title={intl.formatMessage(messages.room)} onClick={() => setPipTypeFilter(ENTITIES.VIDEO_ROOM)}>
						<CameraIndoor fontSize="10" />
					</div>
					<div title={intl.formatMessage(messages.screen)} onClick={() => setPipTypeFilter(STREAM_TYPES.SCREEN)}>
						<Monitor fontSize="10" />
					</div>
					<div title={intl.formatMessage(messages.camera)} onClick={() => setPipTypeFilter(STREAM_TYPES.WEBCAM)}>
						<PhotoCameraFront fontSize="10" />
					</div>
					<div title={intl.formatMessage(messages.mobile)} onClick={() => setPipTypeFilter(STREAM_TYPES.MOBILE)}>
						<Smartphone fontSize="10" />
					</div>
				</div>
				<div className="title">{intl.formatMessage(messages[pipTypeFilter])}</div>
				<div className="close" title={intl.formatMessage(messages.close)} onClick={() => dispatch(removePip(pipId))}>
					<Close fontSize="10" />
				</div>
				<VideoBox pipTypeFilter={pipTypeFilter} pipId={pipId} />
			</Rnd>
		</div>
	);
};

Pip.propTypes = {
	pipId: PropTypes.number.isRequired,
};

export const PipLayer = () => {
	const pips = useSelector(getPips);

	// Single sync interval shared by all open PiP windows - re-syncs PiP videos to the main video's
	// time. Never touches the main video itself; play/pause/seek elsewhere still go through syncVideos.
	useInterval(
		() => {
			const mainVideo = getMainVideo();
			if (!mainVideo) return;

			const mainTime = mainVideo.currentTime;

			document.querySelectorAll('video.examVideo').forEach((video) => {
				if (video === mainVideo) return;
				// Not loaded yet - nothing to sync or resume
				if (video.readyState === 0) return;
				// Don't push a PiP video past its own (possibly shorter) duration; skip the check
				// when duration isn't known yet (NaN on a not-yet-loaded video)
				if (!isNaN(video.duration) && mainTime > video.duration) return;
				if (Math.abs(video.currentTime - mainTime) > PIP_SYNC_TOLERANCE_SECONDS) {
					video.currentTime = mainTime;
				}
				// Main seeked back within this video's range after it had paused at its own end -
				// resume it (guarded by !mainVideo.paused so a globally paused state stays paused)
				if (!mainVideo.paused && video.paused) {
					video.play().catch(() => {});
				}
			});
		},
		pips.length > 0 ? 2000 : null
	);

	return pips.map((p) => <Pip key={p.id} pipId={p.id} />);
};
